import { Box, Paper, Typography } from "@mui/material";
import { Verified as VerifiedIcon } from "@mui/icons-material";
import { submissionMarkerKey } from "../../../services/storageKeys";

type Marker = {
  taskVisibleId?: string;
  submittedAt?: string;
  mobile?: string;
};

export function readSubmittedMarker(
  taskId: string | null,
  currentMobile: string | null,
): Marker | null {
  if (!taskId) return null;
  try {
    const raw = localStorage.getItem(submissionMarkerKey(taskId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Marker;
    // Mobile-scoped so a different vendor on the same device doesn't inherit the marker.
    if (!parsed.mobile || parsed.mobile !== currentMobile) return null;
    return parsed;
  } catch {
    return null;
  }
}

function formatDate(iso: string | undefined): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return null;
  }
}

type Props = { marker?: Marker | null };

export function PostSubmissionState({ marker }: Props) {
  const submittedOn = formatDate(marker?.submittedAt);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F5F7FC" }}>
      <Box sx={{ maxWidth: 640, mx: "auto", px: 3, py: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: 5,
            border: 1,
            borderColor: "#E0E4EF",
            borderRadius: "12px",
            bgcolor: "background.paper",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              mx: "auto",
              mb: 3,
              borderRadius: "50%",
              bgcolor: "#E6F4EA",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <VerifiedIcon sx={{ fontSize: 40, color: "#1B7C4E" }} />
          </Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 1 }}>
            Application submitted
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mb: 3, lineHeight: 1.6 }}
          >
            Your vendor onboarding application is under review. Our team
            will email you as soon as there is an update.
          </Typography>

          {(marker?.taskVisibleId || submittedOn) && (
            <Box
              sx={{
                bgcolor: "#FAFBFF",
                border: 1,
                borderColor: "#E0E4EF",
                borderRadius: "8px",
                px: 3,
                py: 2,
                display: "grid",
                gridTemplateColumns: marker?.taskVisibleId && submittedOn
                  ? "1fr 1fr"
                  : "1fr",
                columnGap: 3,
                rowGap: 1.5,
                textAlign: "left",
              }}
            >
              {marker?.taskVisibleId && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", display: "block" }}
                  >
                    Reference
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, fontFamily: "monospace" }}>
                    {marker.taskVisibleId}
                  </Typography>
                </Box>
              )}
              {submittedOn && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", display: "block" }}
                  >
                    Submitted
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    {submittedOn}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
